import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiService } from './api.service';
import { Observable, forkJoin, of } from 'rxjs';
import { LocalStorageService } from '@app/shared/services/storage/local-storage.service';
import { map, tap } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { flatMap } from 'lodash';
import { ViewMunicipalitiesLumenPerWattRanking } from '@app/shared/models/energiekarte/ViewMunicipalitiesLumenPerWattRanking';
import { NullableString } from '@app/shared/models/nullable';
import { User } from '@app/shared/models/user';
@Injectable({
  providedIn: 'root',
})
export class MunicipalityService extends ApiService {
  municipalityConciseCache: MunicipalityView[] = [];
  municipalityCache: Municipality[] = [];

  constructor(public http: HttpClient, public localStorageService: LocalStorageService, private cookieService: CookieService) {
    super(http);
  }

  authzViewsUri = 'socrates/authz/api/views';
  baseUri = 'socrates/authz/api';
  middleTierBaseUri = 'api/municipalities';
  streetlightUri = 'socrates/streetlights/api';
  operationsUri = 'socrates/operations/api';
  municipalitiesDbColumnNames = [
    'Guid',
    'ItemId',
    'MunicipalityKey',
    'MunicipalityNumber',
    'MunicipalityTypeId',
    'RegionalKey',
    'Name',
    'AdministrationCollectivityId',
    'AdministrationCollectivity',
    'CustomerCenterId',
    'CustomerCenter',
    'PrimaryAdminGuid',
    'MailSendWorkOrderTo',
    'ZipCode',
    'ColorCode01',
    'ColorCode02',
    'ColorCode03',
    'LastDataImportDate',
    'LastLightPerformanceCalculationDate',
    'SumWattCurrent',
    'SumLumenCurrent',
    'SumWattOptimized',
    'SumLumenOptimized',
    'GrossElectricityPricePerUnit',
    'Co2Emission',
    'ImageName',
    'IsGeneralReportingDamageEnabled',
    'IsInTestMode',
    'IsActive',
    'IsDeactivated',
    'IsDeleted',
    'CreationDate',
    'ModifyDate',
    'ModifyUser',
    'EnergyMonitorUrl',
    'ClimateNaviUrl',
    'LocalElectricityMarketUrl',
    'IsProposedMeterReadingEnabled',
    'DmgRepPWAActive',
    'RoomMonitoringUrl',
  ];
  private _municipalityViewDefaultSelection: string[] = [
    'AdministrationCollectivityId',
    'Guid',
    'IsDeactivated',
    'MunicipalityKey',
    'Name',
    'PrimaryAdminGuid',
    'ZipCode',
  ];

  private regionMunicipalitiesSelection: string[] = ['MunicipalityKey', 'Name', 'ZipCode', 'IsDeactivated'];

  addMunicipality(municipalityKeyString: string) {
    const request = {
      municipalites: municipalityKeyString,
    };
    return this.http.post(this.endpoint(this.operationsUri, 'AddMunicipality'), request);
  }

  private getEndpoint(includeDeactivated: boolean): string {
    if (includeDeactivated === false) {
      return this.endpoint(
        this.baseUri,
        `municipalities?$filter=isDeactivated eq ${includeDeactivated}&$select=${this._municipalityViewDefaultSelection.join(',')}`
      );
    } else {
      return this.endpoint(this.baseUri, `municipalities?$select=${this._municipalityViewDefaultSelection.join(',')}`);
    }
  }

  public getAll(includeDeactivated: boolean, useCache = false): Observable<MunicipalityView[]> {
    if (useCache && this.municipalityConciseCache.length > 0) {
      return of(this.municipalityConciseCache);
    }

    return this.http.get<MunicipalityViewResponse[]>(this.getEndpoint(includeDeactivated)).pipe(
      map((municipalityViewResponses: MunicipalityViewResponse[]): MunicipalityView[] => {
        return municipalityViewResponses.map(
          (municipalityViewResponse: MunicipalityViewResponse) =>
            ({
              ...municipalityViewResponse,
              municipalityName: municipalityViewResponse.name,
            } as MunicipalityView)
        );
      }),
      tap((d) => {
        this.municipalityConciseCache = d;
      })
    );
  }

  getAllMunicipalities = () =>
    this.http.get<[]>(
      this.endpoint(
        this.baseUri,
        `municipalities?$select=MunicipalityKey,Name,ZipCode,CreationDate,ModifyDate,ModifyUser,IsActive,IsDeactivated,IsDeleted&$count=true`
      )
    );

  public getAllConcise(): Observable<Municipality[]> {
    return this.http.get<any[]>(this.directEndpoint(`api`, `userregistration/municipalities`));
  }

  public getByMunicipalityGuid(guid: string): Observable<Municipality[]> {
    return this.http.get<Municipality[]>(this.endpoint(this.baseUri, `municipalities?$filter=guid eq ${guid}`));
  }

  public getKamByMunicipalityGuid(guid: string): Observable<User> {
    return this.http.get<User>(this.directEndpoint(this.middleTierBaseUri, guid, 'kamdetails'));
  }

  public getByMunicipalityKey(municipalityKey: string): Observable<Municipality[]> {
    return this.http.get<Municipality[]>(this.endpoint(this.baseUri, `municipalities?$filter=municipalityKey eq '${municipalityKey}'`));
  }

  public getPanelSettings(municipalityKey: string): Observable<Array<{ grossElectricityPricePerUnit: number }>> {
    return this.http.get<Array<{ grossElectricityPricePerUnit: number }>>(
      this.endpoint(this.baseUri, `municipalities?$filter=municipalityKey eq '${municipalityKey}'&$select=grossElectricityPricePerUnit`)
    );
  }

  getByRegionIds(regionIds: string[]): Observable<RegionMunicipality[]> {
    return this.http
      .get<MunicipalitiesResponse[]>(
        this.endpoint(
          this.baseUri,
          `municipalities?$select=${this.regionMunicipalitiesSelection.join(
            ','
          )}&$expand=RegionMunicipalities($select=MunicipalityId,RegionId;$filter=regionId in (${regionIds.join(',')}))&$filter=isDeactivated eq false`
        )
      )
      .pipe(
        map((municipalities: MunicipalitiesResponse[]): RegionMunicipality[] =>
          flatMap(
            municipalities.map((municipalityResponse: MunicipalitiesResponse) =>
              municipalityResponse.regionMunicipalities.map(
                (regionMunicipality: RegionMunicipalities) =>
                  ({
                    isDeactivated: municipalityResponse?.isDeactivated,
                    municipalityId: regionMunicipality?.municipalityId,
                    municipalityKey: municipalityResponse?.municipalityKey,
                    municipalityName: municipalityResponse?.name,
                    regionId: regionMunicipality?.regionId,
                    zipCode: municipalityResponse?.zipCode,
                  } as RegionMunicipality)
              )
            )
          )
        )
      );
  }

  getByPrimaryKAM(primaryAdminGuid: string) {
    return this.http.get<Municipality[]>(this.endpoint(this.baseUri, `municipalities?$filter= primaryAdminGuid eq ${primaryAdminGuid}`));
  }

  public updateMunicipality(data: any) {
    this.localStorageService.removeItem(this.endpoint(this.baseUri, 'municipalities/' + data.municipalityKey));
    const payload = this.patchMapColumnNames(data, this.municipalitiesDbColumnNames);
    return this.http.patch(this.endpoint(this.baseUri, 'municipalities/' + data.municipalityKey), payload);
  }

  updatePrimaryKAMofMunicipalities(municipalityKeys: string[], guid: string): Observable<any[]> {
    const observable: Observable<any>[] = [];
    municipalityKeys.forEach((municipalityKey: string) => {
      const body = { MunicipalityKey: municipalityKey, PrimaryAdminGuid: guid };
      observable.push(this.http.patch(this.endpoint(this.baseUri, 'municipalities/' + municipalityKey), body));
    });
    return forkJoin(observable);
  }

  public updateMunicipalityByKey(data: any) {
    return this.updateMunicipality(data);
  }

  public uploadImage(id: string, path: string, rawFile: File, fileName: string) {
    const formData = new FormData();
    formData.append('Path', path);
    formData.append('File', rawFile, fileName);
    return this.http.post(this.directEndpoint(this.middleTierBaseUri, `${id}/image`), formData);
  }

  public getLumenPerWattRangking(): Observable<ViewMunicipalitiesLumenPerWattRanking[]> {
    return this.http.get<ViewMunicipalitiesLumenPerWattRanking[]>(this.endpoint(this.streetlightUri, `views/ViewMunicipalitiesLumenPerWattRanking`));
  }

  public getApplicationUrl(municipalityId: string): Observable<string[]> {
    return this.http
      .get<MunicipalityPermissionsMappings[]>(this.endpoint(this.baseUri, `/municipalities/${municipalityId}/permissions?$select=applicationURL`))
      .pipe(map((response) => response.map((item) => item.applicationURL)));
  }
}

export interface Municipality extends EditableMunicipalityConfig {
  itemId: number;
  municipalityNumber: string;
  regionalKey: string;
  description: string;
  administrationCollectivityId: string;
  administrationCollectivityKey: string;
  colorCode01: string;
  colorCode02: string;
  colorCode03: string;
  lastDataImportDate?: Date;
  lastLightPerformanceCalculationDate?: Date;
  sumWattCurrent?: number;
  sumLumenCurrent?: number;
  sumWattOptimized?: number;
  sumLumenOptimized?: number;
  grossElectricityPricePerUnit?: number;
  co2Emission?: number;
  isInTestMode: boolean;
  isProposedMeterReadingEnabled: boolean;
  isActive: boolean;
  isDeleted: boolean;
  creationDate?: Date;
  primaryAdminId: string;
  primaryAdminGuid: string;
  key: string;
  imageName: string;
}

export interface EditableMunicipalityConfig {
  guid: string;
  climateNaviUrl?: string;
  customerCenter: number;
  customerCenterId: string;
  dmgRepPwaactive: boolean;
  energyMonitorUrl?: string;
  isActive: boolean;
  isDeactivated: boolean;
  isGeneralReportingDamageEnabled: boolean;
  isInTestMode: boolean;
  localElectricityMarketUrl?: string;
  modifyUser?: string;
  municipalityKey: string;
  name: string;
  roomMonitoringUrl?: string;
  zipCode: string;
  applicationUrl?: string;
  okoisPreferred?: boolean;
}

export interface MunicipalityView {
  guid: string;
  municipalityKey: string;
  municipalityName: string;
  administrationCollectivityId: string;
  primaryAdminGuid: NullableString;
  zipCode: string;
  isDeactivated: boolean;
  isOrphan?: boolean;
}

export interface MunicipalityViewResponse {
  guid: string;
  municipalityKey: string;
  name: string;
  administrationCollectivityId: string;
  primaryAdminGuid: NullableString;
  zipCode: string;
  isDeactivated: boolean;
}

export interface RegionMunicipality {
  regionId: string;
  municipalityId: string;
  municipalityKey: string;
  municipalityName: string;
  zipCode: string;
  isDeactivated: boolean;
}

interface MunicipalitiesResponse {
  regionMunicipalities: RegionMunicipalities[];
  municipalityKey: string;
  name: string;
  zipCode: string;
  isDeactivated: boolean;
}

interface RegionMunicipalities {
  regionId: string;
  municipalityId: string;
}

export interface MunicipalityPermissionsMappings {
  applicationURL: string;
}
